import React from 'react'
import { Select } from 'antd'
import './Pagination.scss'
import Button from '../Button/Button'
import { TABLE } from '../../constants/common'

interface PaginationProps {
  totalRecords: number
  pageSize: number
  currentPage: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  dataLength?: number
}

const Pagination: React.FC<PaginationProps> = ({
  totalRecords,
  pageSize,
  currentPage,
  onPageChange,
  onPageSizeChange,
  dataLength = 0,
}) => {
  const totalPages = Math.ceil(totalRecords / pageSize)

  const handlePageSizeChange = (value: number) => {
    onPageSizeChange(value)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
    }
  }

  const handleGoToPage = (page: number) => {
    if (page > 0 && page <= totalPages) {
      onPageChange(page)
    }
  }

  const handleFirstPage = () => {
    if (currentPage > 1) {
      onPageChange(1)
    }
  }

  const handleLastPage = () => {
    if (currentPage < totalPages) {
      onPageChange(totalPages)
    }
  }

  // Generate page numbers (traditional pagination with ellipsis)
  // Pattern: << | < | 1 ... | 9 | 10 | 11 | ... | 30 | > | >>
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const delta = 1 // Number of pages to show on each side of current page

    if (totalPages <= 7) {
      // If total pages is 7 or less, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
      return pages
    }

    // Always show first page
    pages.push(1)

    // Calculate the range of pages to show around current page
    const startPage = Math.max(2, currentPage - delta)
    const endPage = Math.min(totalPages - 1, currentPage + delta)

    // Determine if we need ellipsis after page 1
    if (startPage > 2) {
      pages.push('ellipsis-start')
    }

    // Add pages around current page
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    // Determine if we need ellipsis before last page
    if (endPage < totalPages - 1) {
      pages.push('ellipsis-end')
    }

    // Always show last page
    if (totalPages > 1) {
      pages.push(totalPages)
    }

    return pages
  }

  const pageNumbers = getPageNumbers()

  return (
    <div className="pagination">
      <div>
        <div className="records-selection">
          <div>Items per page</div>
          <div>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              options={TABLE.PAGINATION_OPTIONS}
              style={{ width: 80 }}
              disabled={dataLength === 0}
            />
          </div>

          <div className="page-indicator">
            {/* Items per page  25  1-25 of 500 items */}
            <span>
              showing{' '}
              {Math.min((currentPage - 1) * pageSize + 1, totalRecords)} -{' '}
              {Math.min(currentPage * pageSize, totalRecords)} of{' '}
              {totalRecords} items
            </span>
          </div>
        </div>
      </div>
      <div className="btn-container">
        <Button
          className="btn-nav"
          variant="outlined"
          disabled={currentPage === 1}
          onClick={handleFirstPage}
        >
          &lt;&lt;
        </Button>
        <Button
          className="btn-nav"
          variant="outlined"
          disabled={currentPage === 1}
          onClick={handlePreviousPage}
        >
          &lt;
        </Button>
        {pageNumbers.map((pageNumber, index) => {
          if (pageNumber === 'ellipsis-start' || pageNumber === 'ellipsis-end') {
            return (
              <span key={`ellipsis-${index}`} className="ellipsis">
                ...
              </span>
            )
          }
          return (
            <Button
              variant={currentPage === pageNumber ? 'contained' : 'outlined'}
              key={pageNumber}
              className={`btn-page-number`}
              onClick={() => handleGoToPage(pageNumber as number)}
            >
              {pageNumber}
            </Button>
          )
        })}
        <Button
          className="btn-nav"
          variant="outlined"
          disabled={currentPage === totalPages || dataLength === 0}
          onClick={handleNextPage}
        >
          &gt;
        </Button>
        <Button
          className="btn-nav"
          variant="outlined"
          disabled={currentPage === totalPages || dataLength === 0}
          onClick={handleLastPage}
        >
          &gt;&gt;
        </Button>
      </div>
    </div>
  )
}

export default Pagination
